import useManualQuery from '@/data/hook/query/v1/useManualQuery';
import useStudentReport from '@/data/hook/useStudentReport';
import useToggle from '@/data/hook/useToggle';
import { listPedagogicalSheets } from '@/data/services/pedagogicalSheetServices';
import { studentAssessmentsQuerykeys } from '@/data/services/querykeys';
import { createStudentReport } from '@/data/services/scheduledLessonReportServices';
import { createStudentAssessment } from '@/data/services/studentAssessmentsServices';
import Enrollment, { EnrollmentStatusEnum } from '@/models/Enrollment';
import Klass from '@/models/Klass';
import { PedagogicalSheet } from '@/models/PedagogicalSheet';
import ScheduledLesson from '@/models/ScheduledLesson';
import ScheduledLessonReport, {
  PresenceEnum,
} from '@/models/ScheduledLessonReport';
import { OccurrenceAssessment } from '@/models/StudentAssessment';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ModalWarning from '@/components/ui/custom/modals/ModalWarning';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { activateEnrollment } from '@/data/services/enrollmentServices';
import { useActiveStudents } from './StudentReports.hook';

type StudentPresenceNotifierProps = {
  enrollment: Enrollment;
  klass: Klass;
  scheduledLesson: ScheduledLesson;
  report: ScheduledLessonReport | undefined;
};

export default function StudentPresenceNotifier({
  enrollment,
  klass,
  scheduledLesson,
  report,
}: StudentPresenceNotifierProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'manageScheduledLessonReport',
  });

  const { enrollmentQueryKeys } = useActiveStudents({
    klassId: klass.id,
    active: true,
    enabled: false,
  });
  const studentId = enrollment.student.id;
  const scheduledLessonId = scheduledLesson.id;
  const { invalidateReports } = useStudentReport({
    studentId,
    scheduledLessonId,
  });
  const isAwaitingPayment =
    enrollment.status === EnrollmentStatusEnum.AWAITING_PAYMENT;
  const isFromAnotherUnit = !enrollment.student.unitsIds.includes(klass.unitId);
  const shouldDisplay = isAwaitingPayment && !report && isFromAnotherUnit;

  const { toggle, isOpen } = useToggle();
  const { update } = useManualQuery<Enrollment>({
    queryKey: enrollmentQueryKeys,
  });

  async function setEnrollmentAsActive() {
    if (enrollment.activatedAt) return;
    const enrollmentId = enrollment.id;
    const activatedAt = scheduledLesson.datetime;
    await activateEnrollment({ enrollmentId, studentId }, { activatedAt });
    update({ ...enrollment, activatedAt });
  }

  async function setStudentAsAttended() {
    return await createStudentReport({
      scheduledLesson: scheduledLesson.id,
      student: studentId,
      presence: PresenceEnum.ATTENDED,
    });
  }

  const { addItem } = useManualQuery(
    studentAssessmentsQuerykeys.list({
      studentId,
      scheduledLessonId: [scheduledLessonId],
    }),
  );

  async function getPedagogicalSheet(): Promise<PedagogicalSheet | undefined> {
    const {
      results: [pedagogicalSheet],
    } = await listPedagogicalSheets({ studentId });
    return pedagogicalSheet;
  }

  async function createOccurrence() {
    const { id: pedagogicalSheetId } = (await getPedagogicalSheet()) || {};
    const occurrenceAssessment: Partial<OccurrenceAssessment> = {
      scheduledLessonId,
      description:
        'O aluno acessou a aula mas não está com o material liberado. Favor entrar em contato com o responsável para fazer o pagamento ou libere o material na Loja manualmente.',
    };
    return await createStudentAssessment({
      klassId: klass.id,
      occurrenceAssessment,
      pedagogicalSheetId,
    });
  }

  async function onConfirm() {
    await setEnrollmentAsActive();
    const scheduledLessonReport = await setStudentAsAttended();
    const studentAssessment = await createOccurrence();
    return { studentAssessment, scheduledLessonReport };
  }

  const { isLoading, mutate } = useMutation({
    mutationFn: onConfirm,
    async onSuccess({ studentAssessment }) {
      await invalidateReports();
      addItem(studentAssessment);
      alert.success(t('saved'));
      toggle();
    },
    onError(error) {
      alert.error(getErrorMessage(error));
    },
  });

  return (
    <ConditionalRenderer condition={shouldDisplay}>
      <MainButton
        text={t('presentStudent')}
        className="whitespace-nowrap pointer-events-auto filter-none"
        onClick={toggle}
      />
      <ModalWarning
        onClickCancel={toggle}
        onClickConfirm={() => mutate()}
        translationString="gerateChargesModal"
        visible={isOpen}
        isLoading={isLoading}
        confirmColor="warning"
      />
    </ConditionalRenderer>
  );
}
